import type { Sku, SkuMetadata } from "@generated/types"
import type { Selectable } from "kysely"
import { lowerFirst } from "lodash"
import { z } from "zod"

/** Every field the ERP tab reports on and can reset to its ingested value. */
export const ERP_FIELDS = [
  "totalLeadTimeDays",
  "moq",
  "secondaryMoq",
  "colliSize",
  "buffer",
  "preferredVendorId",
  "productType",
  "noosStart",
  "noosEnd",
] as const

export type ErpField = (typeof ERP_FIELDS)[number]

/** The subset a company can hand to its ERP entirely. Every other `ErpField` is resettable only. */
export const ERP_LOCKABLE_FIELDS = [
  "totalLeadTimeDays",
  "secondaryMoq",
  "preferredVendorId",
  "productType",
] as const satisfies readonly ErpField[]

export type ErpLockableField = (typeof ERP_LOCKABLE_FIELDS)[number]

export const erpLockableFieldSchema = z.enum(ERP_LOCKABLE_FIELDS)

/**
 * Which SkuMetadata (custom) and Sku (ERP) columns back each field. A field counts as "set" when
 * any of its columns is non-null, so it may own several: buffer needs a type alongside its value.
 */
export const ERP_FIELD_SOURCES: Record<
  ErpField,
  { custom: readonly (keyof Selectable<SkuMetadata>)[]; erp: readonly (keyof Selectable<Sku>)[] }
> = {
  totalLeadTimeDays: { custom: ["totalLeadTimeDays"], erp: ["totalLeadTimeDays"] },
  moq: { custom: ["minimumOrderQuantity"], erp: ["skuMoq"] },
  secondaryMoq: { custom: ["secondaryMoq"], erp: ["secondaryMoq"] },
  colliSize: { custom: ["colliSize"], erp: ["colliSize"] },
  buffer: { custom: ["bufferType", "bufferValue"], erp: ["bufferType", "bufferValue"] },
  preferredVendorId: { custom: ["preferredVendorId"], erp: ["preferredVendorId"] },
  productType: { custom: ["productType"], erp: ["productType"] },
  noosStart: { custom: ["activatedFrom"], erp: ["noosStartDate"] },
  noosEnd: { custom: ["activatedUntil"], erp: ["noosEndDate"] },
}

export const ERP_FIELD_LABELS: Record<ErpField, string> = {
  totalLeadTimeDays: "Total lead time",
  moq: "SKU MOQ",
  secondaryMoq: "Secondary MOQ",
  colliSize: "Colli size",
  buffer: "Buffer",
  preferredVendorId: "Preferred vendor",
  productType: "Product type",
  noosStart: "NOOS start",
  noosEnd: "NOOS end",
}

/** A label for use mid-sentence: "lead time", while "SKU MOQ" and "NOOS start" keep their caps. */
export const midSentenceLabel = (label: string) => {
  const [firstWord = ""] = label.split(" ")
  return firstWord === firstWord.toUpperCase() ? label : lowerFirst(label)
}

/** A company renames its secondary MOQ after whatever it groups by. */
export function secondaryMoqLabel(groupVariable: string | null | undefined) {
  switch (groupVariable) {
    case "variantId":
      return "Variant MOQ"
    case "productId":
      return "Product MOQ"
    default:
      return ERP_FIELD_LABELS.secondaryMoq
  }
}

export const erpFieldLabelFor = (
  field: ErpField,
  secondaryMoqGroupVariable: string | null | undefined,
) =>
  field === "secondaryMoq" ? secondaryMoqLabel(secondaryMoqGroupVariable) : ERP_FIELD_LABELS[field]

export const ERP_CONTROLLED_TOOLTIP =
  "This field is controlled by your ERP and can't be edited in Hakio."

export const erpControlledMessage = (
  field: ErpLockableField,
  secondaryMoqGroupVariable: string | null | undefined,
) =>
  `"${erpFieldLabelFor(field, secondaryMoqGroupVariable)}" is controlled by your ERP and cannot be edited in Hakio.`

const isErpLockableField = (field: string): field is ErpLockableField =>
  (ERP_LOCKABLE_FIELDS as readonly string[]).includes(field)

export const SKU_METADATA_COLUMN_TO_ERP_FIELD = Object.fromEntries(
  ERP_LOCKABLE_FIELDS.flatMap((field) =>
    ERP_FIELD_SOURCES[field].custom.map((column) => [column, field] as const),
  ),
) as Partial<Record<string, ErpLockableField>>

/** Drops keys left behind by renamed fields, which would lock an input nothing can unlock. */
export function readControlledFields(persisted: string[] | null | undefined): ErpLockableField[] {
  return (persisted ?? []).filter(isErpLockableField)
}
